import { Button, EButtonColor, HeroSection, Section, SectionSubtitle, SectionTitle } from '@web_opxp/components';
import { AuthSelectors, LanguageSelectors, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { SKELETON_CLASSNAME, SKELETON_DUMMYTEXT } from '../../base/constants';
import { IDomainCalculation } from '../../base/types';
import SideStory from '../../components/SideStory/SideStory';
import { CyclesSelectors } from '../../cycles';
import { IState } from '../../store/state';
import { TrainingIntensitySelectors } from '../../trainingIntensity';
import { AssessmentProgress, ProgressPageState } from '../AssessmentProgress';
import { getDomains, isAssessmentStarted } from '../selectors';
import { AssessmentActions } from '../slice';
import { filterCFQEvaluationDomains, removeCFQDomains } from '../utils';
import CFQEvaluation from './CFQEvaluation';
import './EvaluationSubpage.scss';
import ResultCards from './ResultCards';
import { MapDomainIds } from './constants';

export interface IEvaluationSubpageProps {
  domains: IDomainCalculation[];
  hasCFQ: boolean;
  loading: boolean;
  authorized: boolean;
  started: boolean;
  onStart: () => void;
  onCreatePlanClicked: () => void;
}

export const EvaluationSubpage: React.FC<IEvaluationSubpageProps> = ({
  domains,
  hasCFQ,
  loading,
  authorized,
  started,
  onStart,
  onCreatePlanClicked,
}) => {
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!authorized || started) {
      return;
    }
    onStart();
  });

  const evaluationLoading = loading || domains.length === 0;
  const skeletonClass = evaluationLoading ? SKELETON_CLASSNAME : '';
  const trainingDomains = removeCFQDomains(domains);
  const cfqDomains = filterCFQEvaluationDomains(domains);
  const weakness = evaluationLoading ? null : trainingDomains[0];
  const domainId = weakness ? weakness.domainId : null;
  const weaknessDomainCode = domainId ? MapDomainIds[domainId] : '';
  const evaluationDescription = evaluationLoading
    ? SKELETON_DUMMYTEXT
    : t(`postreg:Evaluation.Description.${weaknessDomainCode}`);

  const handleCreatePlanClick = () => onCreatePlanClicked();

  return (
    <>
      <HeroSection lottie={weaknessDomainCode} lottieLoop={false}>
        <SectionTitle className={skeletonClass}>{t('postreg:Evaluation.Title')}</SectionTitle>
        <SectionSubtitle className={skeletonClass}>{evaluationDescription}</SectionSubtitle>
        <Button
          className={skeletonClass}
          color={EButtonColor.white}
          label={t('postreg:Evaluation.CreateTrainingPlan')}
          onClick={handleCreatePlanClick}
          disabled={evaluationLoading}
        />
      </HeroSection>
      <Section>
        <AssessmentProgress page={ProgressPageState.EvaluationDone} cfq={hasCFQ} />
      </Section>
      <Section>
        <div className="row">
          <div className="col-6">
            <ResultCards domains={trainingDomains} skeletonClass={skeletonClass} />
          </div>
          <div className="col-2" />
          <div className="col-4">
            <SideStory domain={domainId} />
          </div>
        </div>
      </Section>
      {hasCFQ && cfqDomains.length > 0 ? <CFQEvaluation domains={cfqDomains} /> : null}
    </>
  );
};

export const mapStateToProps = (state: IState) => {
  const domains = getDomains(state);
  const evaluationLoading = TrainingIntensitySelectors.isEvaluationLoading(state);
  const languageLoading = LanguageSelectors.isLoading(state);
  const hasCFQ = CyclesSelectors.doesCurrentSessionHasCFQWorkouts(state);
  const authorized = AuthSelectors.isAuthorized(state);
  const started = isAssessmentStarted(state);
  return {
    domains,
    hasCFQ,
    authorized,
    started,
    loading: evaluationLoading || languageLoading,
  };
};

export const mapDispatchToProps = {
  onStart: AssessmentActions.initAssessment,
  onCreatePlanClicked: AssessmentActions.createTrainingPlanClick,
};

export default connect(mapStateToProps, mapDispatchToProps)(EvaluationSubpage);
